import { cssClass } from '@/shared/utils'
import { useHighlights } from '../hooks/useHighlights'
import Masonry from 'react-masonry-css'
import { HighlightCard } from './highlight-card'
import { HighlightCardLoadig } from './highlight-card/HighlightCadLoading'
import {
  useIsOwnProfile,
  useProfile,
} from 'containers/profile/hooks/useProfile'
import { Title } from '@/components/commons/typography/Title'
import { ButtonComponent } from '@/components/commons'
import { HightlightFormModal } from './HighlightFormModal'
import { useMemo, useRef, useState, useContext } from 'react'
import { joinWithSlash } from 'utilities/links'
import { Highlight } from '../types/highlights'
import { HighlightInModal } from './highlight-card/HighlightInModal'
import { useHighlightEditorStore } from '../store/highlightEditor'
import { useHighlightCardModal } from '../hooks/useHighlightCardModal'
import { HighlightMenu } from './highlight-list/HighlightMenu'
import { HighlightEmptyState } from './highlight-list/HighlightEmptyState'
import { BrowserContext } from 'context'

interface HighlightsListProps {
  showFullList?: boolean
}
export const HighlightsList = ({ showFullList }: HighlightsListProps) => {
  const { highlights: originalHighlights, isLoading } = useHighlights()
  const { profile } = useProfile()
  const { isMobile } = useContext(BrowserContext)
  const ownProfile = useIsOwnProfile()
  const { setHighlight } = useHighlightEditorStore()
  const higlightsListRef = useRef<HTMLDivElement>(null)
  const { openModal, closeModal, highlightModalOpen, selectedHighlight } =
    useHighlightCardModal(showFullList, higlightsListRef)

  const [showModal, setShowModal] = useState(false)
  const [editingList, setEditingList] = useState(false)

  const title = showFullList
    ? ownProfile
      ? 'Your Highlights'
      : `${profile?.firstName}'s Highlights`
    : `Highlights`

  const showTitle =
    showFullList || ownProfile || (originalHighlights?.length || 0) > 0

  const breakpointColumnsObj = {
    default: showFullList ? 3 : 2,
    1250: 2,
    600: 1,
  }

  const allHighlights = useMemo(
    () => originalHighlights?.length || 0,
    [originalHighlights]
  )

  const highlights = useMemo(() => {
    if (showFullList) return originalHighlights

    return originalHighlights?.slice(0, 4)
  }, [originalHighlights, showFullList])

  const editHighlight = (highlight: Highlight) => {
    setHighlight(highlight)
    setShowModal(true)
  }

  const tryHighlightsTour = () => {
    const DESKTOP_TOUR = 53419
    const MOBILE_TOUR = 53417
    const tour = isMobile ? MOBILE_TOUR : DESKTOP_TOUR

    try {
      const toursApi = window?.productFruits?.api?.tours
      const tourObj = toursApi?.getTours()?.find((t) => t.id === tour)
      const tourIsValid = tourObj?.rulesValid() || false

      // When both a mobile and desktop tour exist, one can be completed
      // before the other. If that's the case, we don't want to stop the
      // user action as they've already satisfied the tour.
      //
      // EX. User completetes desktop tour, but not mobile. If the user tries
      // this button on mobile, the mobile tour will say it's not valid to display,
      // in which case we should open the modal and return.
      if (!tourIsValid) {
        setShowModal(true)
        return
      }

      const notFinished = tourObj?.userState !== 'finished'

      if (notFinished) {
        toursApi?.listen('tour-finished', (e) => {
          if (e === tour) {
            setShowModal(true)
          }
        })

        toursApi?.tryStartTour(tour)
      } else {
        setShowModal(true)
      }
    } catch (error) {
      setShowModal(true)
    }
  }

  if (isLoading) {
    return (
      <div
        className={cssClass(['px-20 px-0--tablet', showFullList && 'mt-20'])}
      >
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="creatorland-masonry-grid"
          columnClassName="creatorland-masonry-grid_column"
        >
          <HighlightCardLoadig />
          <HighlightCardLoadig />
        </Masonry>
      </div>
    )
  }

  if ((!highlights || highlights.length === 0) && !showFullList && !ownProfile)
    return null

  if ((!highlights || highlights.length === 0) && ownProfile) {
    return (
      <div
        className={cssClass(['px-20 px-0--tablet', showFullList && 'mt-20'])}
      >
        <HightlightFormModal
          show={showModal}
          onClose={() => setShowModal(false)}
        />
        <Title sizeClass="text-16 text-20--tablet mb-20">Highlights</Title>
        <HighlightEmptyState
          onAddHighlight={tryHighlightsTour}
          className={showFullList && 'mt-20'}
        />
      </div>
    )
  }

  return (
    <div
      id={showFullList ? 'highlights' : 'highlights-overview'}
      className={cssClass(['px-0--tablet', showFullList && 'mt-20'])}
    >
      <HightlightFormModal
        show={showModal}
        onClose={() => setShowModal(false)}
      />
      <div className="flex justify-space-between items-center px-20 px-0--tablet">
        {showTitle && (
          <Title sizeClass="text-16 text-20--tablet">{title}</Title>
        )}

        {ownProfile && (
          <HighlightMenu
            onCreate={() => setShowModal(true)}
            onEdit={() => setEditingList(true)}
            isEditing={editingList}
            onComplete={() => setEditingList(false)}
          />
        )}
      </div>
      <div
        ref={higlightsListRef}
        className={cssClass([
          'px-20 px-0--tablet mt-20',
          showFullList && 'mt-20',
        ])}
      >
        <Masonry
          key={profile?.usernameLower + 'highlights'}
          breakpointCols={breakpointColumnsObj}
          className="creatorland-masonry-grid"
          columnClassName="creatorland-masonry-grid_column"
        >
          {highlights?.map((highlight) => (
            <HighlightCard
              key={highlight.id}
              highlight={highlight}
              defaultOpen={showFullList}
              isEditingList={editingList}
              onEdit={() => editHighlight(highlight)}
              onOpen={() => openModal(highlight)}
            />
          ))}
        </Masonry>
      </div>

      {allHighlights > 0 && !showFullList && (
        <ButtonComponent
          variation="white-bg"
          size="xSmall"
          fullWidth
          to={joinWithSlash(profile?.usernameLower, 'highlights')}
          label={`View All (${allHighlights.toLocaleString()})`}
        />
      )}

      <HighlightInModal
        highlight={selectedHighlight}
        closeModal={closeModal}
        isOpen={highlightModalOpen}
      />
    </div>
  )
}
